import "@/App.css";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import NicheDetail from "@/pages/NicheDetail";
import History from "@/pages/History";
import Journey from "@/pages/Journey";
import Admin from "@/pages/Admin";
import RenderLog from "@/pages/RenderLog";
import ChannelKit from "@/pages/ChannelKit";
import ChannelSearch from "@/pages/ChannelSearch";
import CreateChannel from "@/pages/CreateChannel";
import ChannelKitDetail from "@/pages/ChannelKitDetail";
import Characters from "@/pages/Characters";
import TopEarners from "@/pages/TopEarners";
import TopRevenue from "@/pages/TopRevenue";
import MidRevenue from "@/pages/MidRevenue";
import LowRevenue from "@/pages/LowRevenue";
import NicheOpportunities from "@/pages/NicheOpportunities";
import TopVideos30d from "@/pages/TopVideos30d";
import TopNiches30d from "@/pages/TopNiches30d";
import StyleGallery from "@/pages/StyleGallery";
import AutoVideo from "@/pages/AutoVideo";
import AutoVideos from "@/pages/AutoVideos";
import SavedNiches from "@/pages/SavedNiches";
import SavedKits from "@/pages/SavedKits";
import ActiveChannels from "@/pages/ActiveChannels";
import ActiveChannelDetail from "@/pages/ActiveChannelDetail";
import SceneToVideo from "@/pages/SceneToVideo";
import SceneDescriptionCreate from "@/pages/SceneDescriptionCreate";
import StoryboardList from "@/pages/StoryboardList";
import StoryboardDetail from "@/pages/StoryboardDetail";
import CreateStory from "@/pages/CreateStory";
import Settings from "@/pages/Settings";
import ReviewApprove from "@/pages/ReviewApprove";
import MyStoryIdeas from "@/pages/MyStoryIdeas";
import GenerateStoryIdeas from "@/pages/GenerateStoryIdeas";
import Cost from "@/pages/Cost";
import RollYourOwn from "@/pages/RollYourOwn";
import Sidebar from "@/components/Sidebar";
import DeployStatusBanner from "@/components/DeployStatusBanner";
import BuildIndicator from "@/components/BuildIndicator";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import Landing from "@/pages/Landing";

function ProtectedShell({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center font-mono text-sm text-zinc-500">
        [recon] verifying clearance<span className="blink">_</span>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (adminOnly && !user.is_admin) {
    return <Navigate to="/journey" replace />;
  }
  return (
    <div className="min-h-screen bg-black flex flex-col">
      <ImpersonationBanner />
      <DeployStatusBanner />
      <div className="flex flex-1 min-h-0">
        <Sidebar />
        <main className="flex-1 min-w-0">{children}</main>
      </div>
      <BuildIndicator />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Detect session_id in URL fragment synchronously during render to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/journey"
        element={
          <ProtectedShell>
            <Journey />
          </ProtectedShell>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedShell>
            <Dashboard />
          </ProtectedShell>
        }
      />
      <Route
        path="/saved"
        element={
          <ProtectedShell>
            <Dashboard bookmarkedOnly />
          </ProtectedShell>
        }
      />
      <Route
        path="/saved-niches"
        element={
          <ProtectedShell>
            <SavedNiches />
          </ProtectedShell>
        }
      />
      <Route
        path="/saved-kits"
        element={
          <ProtectedShell>
            <SavedKits />
          </ProtectedShell>
        }
      />
      <Route
        path="/niches/:id"
        element={
          <ProtectedShell>
            <NicheDetail />
          </ProtectedShell>
        }
      />
      <Route
        path="/niches/:id/channel-kit"
        element={
          <ProtectedShell>
            <ChannelKit />
          </ProtectedShell>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedShell adminOnly>
            <History />
          </ProtectedShell>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedShell>
            <ChannelSearch />
          </ProtectedShell>
        }
      />
      <Route
        path="/create-channel"
        element={
          <ProtectedShell>
            <CreateChannel />
          </ProtectedShell>
        }
      />
      <Route
        path="/create-channel/kit/:jobId"
        element={
          <ProtectedShell>
            <ChannelKitDetail />
          </ProtectedShell>
        }
      />
      <Route
        path="/characters"
        element={
          <ProtectedShell>
            <Characters />
          </ProtectedShell>
        }
      />
      <Route
        path="/top-earners"
        element={
          <ProtectedShell>
            <TopEarners />
          </ProtectedShell>
        }
      />
      <Route
        path="/top-revenue"
        element={
          <ProtectedShell>
            <TopRevenue />
          </ProtectedShell>
        }
      />
      <Route
        path="/mid-revenue"
        element={
          <ProtectedShell>
            <MidRevenue />
          </ProtectedShell>
        }
      />
      <Route
        path="/low-revenue"
        element={
          <ProtectedShell>
            <LowRevenue />
          </ProtectedShell>
        }
      />
      <Route
        path="/niche-opportunities"
        element={
          <ProtectedShell>
            <NicheOpportunities />
          </ProtectedShell>
        }
      />
      <Route
        path="/top-videos-30d"
        element={
          <ProtectedShell>
            <TopVideos30d />
          </ProtectedShell>
        }
      />
      <Route
        path="/top-niches-30d"
        element={
          <ProtectedShell>
            <TopNiches30d />
          </ProtectedShell>
        }
      />
      <Route
        path="/style-gallery"
        element={
          <ProtectedShell>
            <StyleGallery />
          </ProtectedShell>
        }
      />
      <Route path="/best-to-clone" element={<Navigate to="/niche-opportunities" replace />} />
      <Route
        path="/auto-video/:jobId"
        element={
          <ProtectedShell>
            <AutoVideo />
          </ProtectedShell>
        }
      />
      <Route
        path="/auto-videos"
        element={
          <ProtectedShell>
            <AutoVideos />
          </ProtectedShell>
        }
      />
      <Route
        path="/active-channels"
        element={
          <ProtectedShell>
            <ActiveChannels />
          </ProtectedShell>
        }
      />
      <Route
        path="/active-channels/:channelId"
        element={
          <ProtectedShell>
            <ActiveChannelDetail />
          </ProtectedShell>
        }
      />
      <Route
        path="/scene-to-video"
        element={
          <ProtectedShell>
            <SceneToVideo />
          </ProtectedShell>
        }
      />
      <Route
        path="/scene-description"
        element={
          <ProtectedShell>
            <SceneDescriptionCreate />
          </ProtectedShell>
        }
      />
      <Route
        path="/storyboards"
        element={
          <ProtectedShell>
            <StoryboardList />
          </ProtectedShell>
        }
      />
      <Route
        path="/create-story"
        element={
          <ProtectedShell>
            <CreateStory />
          </ProtectedShell>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedShell>
            <Settings />
          </ProtectedShell>
        }
      />
      <Route
        path="/review"
        element={
          <ProtectedShell>
            <ReviewApprove />
          </ProtectedShell>
        }
      />
      <Route
        path="/ideas"
        element={
          <ProtectedShell>
            <MyStoryIdeas />
          </ProtectedShell>
        }
      />
      <Route
        path="/generate-ideas"
        element={
          <ProtectedShell>
            <GenerateStoryIdeas />
          </ProtectedShell>
        }
      />
      <Route
        path="/storyboards/:storyboardId"
        element={
          <ProtectedShell>
            <StoryboardDetail />
          </ProtectedShell>
        }
      />
      <Route
        path="/cost"
        element={
          <ProtectedShell>
            <Cost />
          </ProtectedShell>
        }
      />
      <Route
        path="/active-channels/:channelId/roll-your-own"
        element={
          <ProtectedShell>
            <RollYourOwn />
          </ProtectedShell>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedShell>
            <Admin />
          </ProtectedShell>
        }
      />
      <Route
        path="/admin/render-log"
        element={
          <ProtectedShell>
            <RenderLog />
          </ProtectedShell>
        }
      />
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              className: "font-mono text-sm",
              style: {
                background: "#0a0a0a",
                border: "1px solid #27272a",
                color: "#fafafa",
                borderRadius: "2px",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
